/**
 * 업데이트 내역의 **노출 대상(역할) 편집** 모달 — 「누가 이 노트를 보나」를 사람이 정하는 자리.
 *
 * 결정 `docs/decisions/2026-10-19-audience-targeting-is-a-namespaced-role-set.md` §2가 「채우는 것은
 * 사람이다」라 적는 자리다 — 노트 본문은 Claude가 커밋에서 파생시키지만 **커밋은 역할을 모른다.**
 *
 * ## 🔴 ⚠️ 2026-10-21 — **이 설정은 현재 노출을 좁히지 않는다. 읽기 전에 이것부터 알아야 한다**
 * 결정 `docs/decisions/2026-10-21-release-note-surfaces-split-anonymous-visibility-from-consumer-reach.md`
 * 의 **채택 2**가 공개 업데이트 내역 페이지에서 `audiences` 판정을 **걷어냈다**(「페이지는 전부 ·
 * 푸시는 타겟팅」) ⇒ 여기서 역할을 골라도 **그 노트는 전원에게 보인다.** 그래서 화면 문구 둘을
 * 정직하게 고쳤다(아래 선택 결과 박스의 🔴 절이 그 경위·공지 쪽과 갈리는 이유를 적는다).
 *  - **「누가 보나」를 정하는 자리**라는 위 첫 문장은 **오늘 참이 아니다** — 저장은 되지만 그 값을
 *    **읽는 소비처가 0개**다(서버 census가 그것을 재고 있다:
 *    `release-notes-audience-reach.guard.spec.ts`의 호출부 래칫 **0자리**).
 *  - 그 값의 미래 소비처는 **채택 3(배지)·5(팝업 푸시)**이고 **둘 다 미구현**이다 ⇒ 「푸시·배지가
 *    소유한다」로 적지 말 것(미구현을 소유자로 적으면 「주인이 있다」로 읽힌다).
 *
 * ⚠️ **이 모달의 입구를 잠글지는 «열린 판단»이다** — 효과 없는 설정을 남길지가 갈린다.
 * 남긴 쪽 논거: 저작은 되돌릴 수 있고(빈 배열로 해제) 잠그면 채택 5가 쓸 데이터를 미리 모을 수
 * 없다. 잠그는 쪽 논거: 역할 겨눔이 prod **0건**이라 모인 것도 없다. ⇒ **2026-10-21 결정의
 * 채택 3·5 시점 판단으로 남았다**(사용자 몫). 그 사이를 안전하게 만드는 것이 위 문구 정직화다.
 * ⇒ 「문구가 이상하다」며 종전 문구로 되돌리지 말 것 — 그것이 **거짓 UI 보증**이었다.
 *
 * ## ⚠️ 이 모달은 `@safetyhub/ui`의 base `Modal`을 쓴다 — raw 패널로 되돌리지 말 것
 * 1차 구현은 `./Overlay` + `<div className="bg-white rounded-lg p-6 …">`로 **raw 모달 패널을 직접
 * 조립**했고 `local/no-untokenized-radius`가 그 `rounded-lg`를 잡았다. **처방은 값 교체가 아니었다** —
 * 그 규칙 메시지가 「raw 모달 패널이면 이 표를 쓰지 마세요 … 답은 값이 아니라 `@safetyhub/ui`의
 * `Modal`/`ConfirmModal`로 **이관**하는 것입니다」라고 직접 말한다. `rounded-lg` → `rounded-modal`로
 * 바꾸면 **eslint는 지나가지만** 줄기 `shared-modal-ux-base`가 닫고 있는 축(legacy raw 모달 15 → 3)에
 * **새 항목을 하나 더하는** 것이 된다 — 그 진척이 되돌아간다.
 *
 * ⇒ base `Modal`로 이관해 그 위반이 **사라졌다**(패널을 직접 그리지 않으므로). 덤으로 셋을 얻었다:
 * `role="dialog"`·포커스 트랩·opener 포커스 복원(접근성 공백이던 자리 — eslint 설정의 legacy 목록
 * 주석이 「Modal 이관이 그 둘을 같이 해결한다」고 적는다) · dirty 닫기 보호 · `closeLocked`.
 *
 * ⚠️ 작성 당시 admin에는 자체 base(admin 로컬 `Modal`)가 따로 있었고 그것은 **그 자신이 legacy
 * 목록에 오른** raw 패널이라 쓰지 않았다(그쪽을 쓰면 이관 대기 층에 의존을 하나 더 얹는다).
 * 그 파일은 2026-10-30 소비처 14곳을 전부 `@safetyhub/ui`로 옮기고 **삭제했다** — admin에서
 * 모달은 이제 `from '@safetyhub/ui'` 하나뿐이다.
 *
 * ## 이것은 「저작」이 아니다 — 고르는 축이 **하나뿐**이다
 * 제목·본문·분류·날짜·`platforms`는 **노트 파일이 소유**하므로 이 모달에 두지 않는다(그 축을 고치는
 * 일은 `release-notes/notes/*.json`을 고쳐 push하는 일이다). `audiences`만 DB 소유이고, 그래서
 * 2026-10-14에 닫은 저작 드리프트가 되돌아오지 않는다(서버 컨트롤러 클래스 JSDoc이 정본).
 * ⚠️ **이 모달에 입력 칸을 더하지 말 것** — 하나를 더하는 순간 그 경계가 사라진다.
 *
 * ## 접두는 UI가 붙인다 — 사람은 **역할 이름**만 고른다
 * 후보는 `lib/audience-options.ts`가 두 조회 API에서 받아 `buildAudienceRef`로 만든다. 자유 타이핑을
 * 열지 않는 이유: 오타는 **아무에게도 맞지 않는 참조**를 만들고, 그 실패는 「안 보인다」라 **조용하다**
 * (결정 §5). 서버 DTO가 접두 없는 값을 400으로 거부하지만 **역할 key의 실재는 아무도 검증하지
 * 않으므로**(DB 행이다) 오타를 만들지 않는 것이 이 화면의 일이다.
 *
 * ## ⚠️ 빈 선택 = 「모두에게」다 — 「아무에게도」가 아니다
 * 그래서 저장 버튼이 **빈 선택에서도 열려 있다**(설정을 해제하는 유일한 길이다). 「아무의 목록에도
 * 뜨지 않게」는 이 축이 아니라 `platforms`의 `INTERNAL`이고 그것은 노트 파일이 소유한다(결정 §6).
 * 화면이 그 둘을 **문구로** 가른다 — 빈 선택에 「모두에게 보입니다」를 명시한다.
 *
 * ## ⚠️ 「닿지 않는 체계를 제약했다」는 **서버가 거부한다** — 여기서 미리 막지 않는다
 * `platforms: [WEB]` 노트에 `sa:` 제약을 넣으면 의도의 **반대**로 동작하므로(규약 2로 모두에게
 * 보인다) 서버가 400으로 거부하고 **어느 체계가 왜 어긋났는지 이름 들어** 말한다
 * (`ReleaseNotesService.bulkUpdateAudiences` · shared `unreachableAudienceNamespaces`).
 * 저장 실패 시 이 모달은 **닫히지 않고** 그 문구가 토스트로 뜬다(선택을 잃지 않게).
 *
 * ⚠️ 그 판정을 **여기서 미리 하지 않는 이유**: surface → 체계 매핑은 지금 서버에만 있고
 * (`PLATFORM_NAMESPACES` — surface enum마다 달라 shared에 둘 수 없다는 그 함수 docblock의 가름)
 * 여기에 베끼면 **같은 규약의 두 번째 구현**이 생긴다. 그 둘이 갈리는 날 화면은 「고를 수 있다」고
 * 하는데 서버가 거부하거나(혼란) 반대로 **화면이 막는데 서버는 허용**한다(그쪽이 더 나쁘다 —
 * 운영자가 할 수 있는 일을 못 한다고 믿는다). ⇒ 매핑을 shared로 올리면 그때 여기서 **선제 차단**
 * 하는 것이 낫다. 그 전까지 집행의 소유자는 서버 하나다.
 *
 * ⚠️ **그 조건은 이제 «두 자리»를 깨운다 — 한 자리만 고치면 두 화면의 판정이 갈린다.**
 * 전사 공지 저작(`pages/Announcements.tsx`)이 2026-10-20에 **같은 판단을 공유**하기로 했다. 그쪽은
 * `platforms`를 직접 고르므로 **이 파일의 클라이언트측 근사**(「닿는 체계」를 `groups` 전체로 보는
 * 것)를 **쓸 수 없다** — 그러면 `WEB`만 고른 공지에 「슈퍼관리자 전원에게 보여도 됩니까」를 묻는
 * **거짓 승인 요구**가 된다. 그래서 그쪽은 선제 판정을 아예 하지 않고 **서버 400을 받아** 승인란을
 * 띄운다(그 대가로 첫 전송이 왕복을 한 번 더 돈다 — **의도된 거래**다).
 * ⇒ 매핑이 shared로 올라오면 **이 모달과 그 화면을 함께** 고칠 것. 사유·되짚을 조건·재는 법의
 * 정본은 `lib/announcements.ts`의 `ANNOUNCEMENT_UNCONSTRAINED_CODE` JSDoc 한 벌이다.
 *
 * ## ⚠️ 한 체계만 좁히면 다른 체계는 **전원**이 대상이다 — 그 자리를 화면이 말한다
 * 판정 규약 2(「내 체계의 제약이 없으면 통과」) 때문에 `company:`만 고르면 **슈퍼관리자 전원**이
 * 여전히 대상이다. 결정 §4가 그것을 「이 설계의 가장 조용한 자리 — 『sa: 제약을 적는 것을 잊었다』와
 * 『슈퍼관리자 전원이 대상이다』가 같은 값이다」라 적고, **저작 쪽 가드가 짝으로 필요하다**고 지시한다.
 * 그 가드가 여기다. ⚠️ **2026-10-20까지는 「경고 문구」였고 막지 않았다** — 「그것이 의도일 수
 * 있다」는 이유였다. 그 판단은 **절반만 맞았다**: 의도일 수 있는 것은 맞지만, 서버가 같은 상태를
 * **400으로 거부**하게 됐으므로(`AUDIENCE_NAMESPACE_UNCONSTRAINED` — 「잊었다」와 「전원 대상이다」를
 * 서버가 구별할 수 없어 작성자가 말해야 한다) 경고만 두면 운영자가 저장을 누르고 **거부만 보는
 * 막다른 길**이 된다. ⇒ 지금은 **명시 승인 체크박스**이고, 체크 전에는 저장이 **비활성**이며 그
 * 값이 `acknowledgeUnconstrainedNamespaces`로 함께 간다. 입력은 shared
 * `constrainedNamespaces()`가 만든다.
 *
 * ⚠️ 「막지 않는다」로 되돌리지 말 것 — 서버 거부를 받아 줄 자리가 여기밖에 없다.
 */
import { useEffect, useMemo, useState } from 'react';
import { Button, Modal, useModalRequestClose } from '@safetyhub/ui';
import { constrainedNamespaces, withObjectParticle } from '@safetyhub/shared';
import { useAudienceOptions, audienceLabel, splitUnlistedAudiences } from '../lib/audience-options';

interface Props {
  /** 대상 노트 — 단일도 `[note]`로 온다(서버 라우트가 일괄 형상이다) */
  noteIds: string[];
  /** 모달을 열 때의 현재 값. 여러 건을 함께 고를 때는 **첫 건의 값**이 초기 선택이다(아래 ⚠️) */
  initialAudiences: string[];
  /** 여러 건을 한 번에 고쳤나 — 문구가 달라진다 */
  multiple: boolean;
  onClose: () => void;
  /**
   * @param acknowledgeUnconstrained 「좁히지 않은 체계의 전원이 대상이 된다」를 운영자가 확인했나.
   *   승인이 **필요 없을 때는 `undefined`**로 온다(보내지 않는다 — 서버 기본값이 거부 쪽이고
   *   그 기본값의 소유자는 서버 하나다).
   */
  onSave: (audiences: string[], acknowledgeUnconstrained?: boolean) => Promise<void>;
}

export default function ReleaseNoteAudienceModal({
  noteIds,
  initialAudiences,
  multiple,
  onClose,
  onSave,
}: Props) {
  const { groups, isLoading, hasFailure } = useAudienceOptions(true);
  const [selected, setSelected] = useState<string[]>(initialAudiences);
  const [saving, setSaving] = useState(false);

  // 대상이 바뀌면(다른 노트를 열면) 선택을 그 노트의 값으로 되돌린다 — 모달을 닫지 않고
  // 재사용될 수 있고, 남은 선택이 다른 노트에 저장되면 **조용한 오설정**이 된다.
  useEffect(() => {
    setSelected(initialAudiences);
    // ⚠️ `initialAudiences`는 배열이라 매 렌더 **새 참조**일 수 있다 — 참조를 dep로 쓰면 이
    //    effect가 매 렌더 돌아 사용자의 선택을 되돌린다. 그래서 **값**으로 묶어 비교한다.
    //    구분자가 `,`인 것은 두 체계의 역할 식별자(kebab key · `co-<cuid>` · SA 대문자 code)에
    //    쉼표가 쓰이지 않기 때문이다(보이지 않는 문자를 쓰면 소스가 읽을 수 없게 된다).
  }, [initialAudiences.join(',')]);

  const toggle = (ref: string) => {
    setSelected((prev) => (prev.includes(ref) ? prev.filter((r) => r !== ref) : [...prev, ref]));
  };

  /**
   * 후보에 없는 저장값 — **「없어진 역할」과 「못 받은 체계의 역할」로 갈라서** 받는다.
   *
   * 판정·사유의 정본은 `lib/audience-options.ts`의 {@link splitUnlistedAudiences} JSDoc 한 벌이다.
   * ⚠️ 여기서 `!groups.some(…)` 한 줄로 되돌리지 말 것 — 그러면 403으로 못 받은 체계의 **정상
   * 제약**이 「제거하세요」 통에 섞여 운영자가 그것을 지운다(노출이 넓어진다).
   * ⚠️ 그 식을 **두 번 인라인으로** 쓰지 말 것(종전 모양이다) — 조건과 렌더가 갈려 한쪽만 고치는
   * 상태가 되고, 같은 파일 안이라 눈에 띄지도 않는다.
   */
  const unlisted = useMemo(() => splitUnlistedAudiences(selected, groups), [selected, groups]);

  /**
   * 「한 체계만 좁혔다」 경고 — 위 docblock의 그 자리.
   *
   * 판정은 shared `constrainedNamespaces()`가 만든 집합으로 한다(여기서 접두를 `split`하지 말 것).
   * 비어 있으면(= 모두에게) 경고가 아니다 — 그때는 어느 체계도 좁히지 않은 것이라 규약 2가
   * 「잊었다」와 섞이지 않는다.
   */
  const unconstrained = useMemo(() => {
    const constrained = constrainedNamespaces(selected);
    if (constrained.size === 0) return [];
    return groups.filter((g) => !constrained.has(g.namespace));
  }, [selected, groups]);

  /**
   * 「그 체계의 전원이 대상이 된다」를 **확인했다** — 서버의 `acknowledgeUnconstrainedNamespaces`로 간다.
   *
   * ⚠️ 선택이 바뀌면 **반드시 풀린다**(fail-closed) — 「company만 고르고 확인 → 다른 역할로 옮기기」
   * 처럼 선택을 옮기면 확인의 **대상이 달라지므로**, 남겨 두면 운영자가 보지 않은 조합에 승인이
   * 실린다. 아래 effect가 그 축을 소유하고, 짝 spec의 `[되돌림] 선택을 바꾸면 승인이 «풀린다»`가
   * 그것을 잰다.
   */
  const [acknowledged, setAcknowledged] = useState(false);
  useEffect(() => {
    setAcknowledged(false);
  }, [selected]);

  /**
   * 승인이 **필요한가** — 그리고 그것이 저장을 막는가.
   *
   * ⚠️ **2026-10-20까지 이 자리는 「경고 문구」였고 막지 않았다.** 그런데 서버가 같은 상태를
   * **400으로 거부**하게 됐으므로 경고만 두면 운영자가 저장을 누르고 **거부만 보는 막다른 길**이
   * 된다 — 화면이 「할 수 있다」고 하는데 서버가 거부하는 그 혼란이 이 파일 docblock이 이미
   * 경고하던 형태다. ⇒ 경고를 **명시 승인**으로 바꾸고 그 값을 함께 보낸다.
   *
   * ⚠️ **이 가드의 분모는 `audience-options.ts`의 `groups`이고 그것은 shared 상수에서 파생한다** —
   * 체계가 늘면 그 파일의 `byNamespace`(`Record<AudienceNamespace, …>`)에서 **tsc가 깨지고**
   * `groups`는 `AUDIENCE_NAMESPACE_VALUES`를 돌아 만들어지므로 새 체계가 피커와 여기 `unconstrained`
   * 에 **자동으로 든다.** ⚠️ 단 CI는 admin을 Vite(esbuild)로 빌드해 **타입을 보지 않으므로** 그
   * 게이트는 **로컬 `pnpm exec tsc --noEmit`뿐**이다(형제: `pages/ReleaseNotes.tsx`의 플랫폼 라벨
   * `Record` 절 · web `lib/organizations.ts`의 같은 문장).
   *
   * ⚠️ **2026-10-21 정정 — 이 자리는 「손열거라 여기서 고칠 수 없다」고 적고 있었고 세 다리가 전부
   * 거짓이었다**(재export·`Record`·`VALUES` 파생이 **그 주석을 쓴 12시간 뒤** 들어왔는데 주석은
   * 안 고쳐졌다. 하필 그 주석이 「그 파일을 고칠 때 이 주석을 함께 읽을 것」이라 적고 있었다).
   * 그대로 두면 읽는 사람이 **이미 있는 래칫을 없는 것으로 믿고** 같은 래칫을 또 만들거나
   * 「어차피 tsc가 조용하니」로 그 `Record`를 느슨하게 되돌린다(그 파일 JSDoc이 정확히 그것을
   * 금지한다). ⇒ 「분모가 손열거다」를 되살리지 말 것 — 먼저 그 파일을 열어 볼 것.
   */
  const needsAcknowledgement = unconstrained.length > 0;
  const blockedByAcknowledgement = needsAcknowledgement && !acknowledged;

  const handleSave = async () => {
    setSaving(true);
    try {
      // 승인이 필요 없을 때 `true`를 보내지 않는다 — 서버는 무시하지만, 보내면 「무엇을 승인한
      // 요청인가」가 로그·전사에서 흐려진다(이 칸은 **행위에 대한 확인**이고 저장되는 값이 아니다).
      await onSave(selected, needsAcknowledgement ? acknowledged : undefined);
    } finally {
      setSaving(false);
    }
  };

  /**
   * 저장하지 않은 선택이 있나 — base `Modal`의 dirty 닫기 보호 입력.
   *
   * 수정 모달의 표준이다(결정 `2026-08-16-edit-modal-dirty-gate-standard`) — Esc·오버레이·✕·푸터
   * 취소가 확인 모달을 거친다. 역할을 여럿 고른 뒤 오버레이를 잘못 누르면 그 선택이 통째로
   * 사라지므로, 이 모달은 그 보호가 필요한 쪽이다.
   *
   * ⚠️ 집합 비교다 — 고른 **순서**는 저장값의 의미가 아니므로(서버는 `text[]`에 받은 순서대로
   * 쓰지만 판정은 `some`이다) 순서만 다른 상태를 dirty로 보면 **거짓 확인 모달**이 뜬다.
   */
  const isDirty = useMemo(() => {
    const before = [...initialAudiences].sort().join(',');
    const after = [...selected].sort().join(',');
    return before !== after;
  }, [initialAudiences, selected]);

  return (
    <Modal
      open
      onClose={onClose}
      title="노출 대상 설정"
      // `SIZE_CLASS.lg`가 `max-w-lg`다 — 종전 raw 패널과 **같은 폭**이라 이관으로 화면이 바뀌지
      // 않는다(선례 `CompanyRoleFormModal`은 `xl`이지만 그쪽은 2열 폼이다).
      size="lg"
      isDirty={isDirty}
      // ⚠️ 저장 중 닫기 차단은 **이 prop이 소유한다** — 종전 `Overlay onClose={saving ? undefined : …}`는
      //    오버레이 한 축만 막고 Esc·푸터 취소는 열려 있었다. `closeLocked`는 그 셋과
      //    `useModalRequestClose`까지 한 번에 잠그는 **유일한 축**이다(그 prop JSDoc).
      closeLocked={saving}
      // ⚠️ **스크롤은 본문에 건다** — 패널에 걸면 푸터까지 함께 스크롤돼 역할 목록이 길 때
      //    저장 버튼이 화면 밖으로 밀린다. 역할은 **DB 행**이라 목록이 자란다(2026-10-19 실측
      //    조직 12 + sa 4). 그 축은 그대로이고, 2026-10-26에 **그 일을 공용 base가 맡았다** —
      //    종전에는 본문 div에 `max-h-[60vh] overflow-y-auto`를 손으로 걸었고 이제 이 prop이
      //    같은 일을 한다(패널 높이 제한 + 헤더·푸터 `flex-none` + 본문 `min-h-0 flex-1
      //    overflow-y-auto`). ⚠️ 상한이 `60vh` → `max-h-full`로 **바뀐다**: 오버레이가
      //    `fixed inset-0 … p-4`라 그 content box가 「뷰포트 − 여백」이고 패널이 그 flex
      //    자식이므로, `vh` 손값과 달리 여백을 **두 번 세거나 모자라지** 않는다.
      //    패널 스크롤 선례(`report-composer/ShortcutsModal`)는 푸터가 없는 모달이라 그 축이
      //    없다 — 그 자리를 근거로 이 prop을 되돌리지 말 것.
      bodyScroll
      footer={
        <AudienceModalFooter
          saving={saving}
          onClose={onClose}
          onSave={handleSave}
          blocked={blockedByAcknowledgement}
        />
      }
    >
      {/*
        스크롤은 위 `bodyScroll`이 소유한다 — 그 이유는 그 prop 옆 주석에 있다(여기 다시 적지
        말 것: 두 자리에 적으면 한쪽이 조용히 낡는다).
        ⚠️ 종전에는 여기 `<div className="max-h-[60vh] overflow-y-auto">`가 있었다. 클래스를
        base로 옮기고 나니 **남는 일이 없어** 프래그먼트로 줄였다(div를 남기면 다음 사람이
        「무슨 일을 하나」를 되짚어야 한다). 자식 들여쓰기가 그대로여서 포맷 변경도 0이다.
        ⚠️ 여기에 `overflow-y-auto`를 다시 넣지 말 것 — base 본문이 이미 스크롤 컨테이너라
        **이중 스크롤**이 된다(그 형태의 선례가 `pages/manual/ManualSearch`다).
      */}
      <>
        <p className="text-sm text-gray-500 mb-4">
          {multiple
            ? `선택한 ${noteIds.length}개 항목의 노출 대상을 한꺼번에 바꿉니다.`
            : '이 항목을 어떤 역할에게 보일지 고릅니다.'}
        </p>

        {/*
          ⚠️ 여러 건을 함께 고칠 때 「현재 값」을 **합집합·교집합으로 보여주지 않는다** — 그러면
          저장이 「고른 것으로 교체」인데 화면은 「섞인 상태」를 보여 **무엇이 저장될지 어긋난다**.
          초기 선택은 첫 건의 값이고, 저장은 **전부 같은 값으로 교체**다. 그 사실을 여기서 말한다.
        */}
        {multiple && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            선택한 항목의 노출 대상이 <strong>모두 아래 값으로 교체</strong>됩니다(항목별로 다른 값은
            유지되지 않습니다).
          </div>
        )}

        {hasFailure && (
          /*
            권한 축이 저작 화면과 다르다 — 역할 목록 조회는 `system-settings`·`admin-management`를
            요구한다(`lib/audience-options.ts`의 그 절). 못 받은 체계는 **고를 수 없게 둔다**:
            자유 타이핑으로 열면 오타가 조용한 오설정이 된다.
          */
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            일부 역할 목록을 불러오지 못했습니다. 그 체계는 선택할 수 없습니다 — 역할 목록 조회
            권한(조직 역할: 시스템 설정 / 슈퍼관리자 역할: 관리자 관리)이 필요합니다.
          </div>
        )}

        {isLoading ? (
          <div className="py-8 text-center text-gray-500">역할 목록을 불러오는 중...</div>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <div key={group.namespace}>
                <div className="font-medium text-sm text-gray-700 mb-2">{group.label}</div>
                {group.failed ? (
                  <div className="text-sm text-gray-400">불러오지 못했습니다.</div>
                ) : group.options.length === 0 ? (
                  <div className="text-sm text-gray-400">후보 역할이 없습니다.</div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {group.options.map((opt) => (
                      <label
                        key={opt.ref}
                        className={`flex items-center gap-1.5 px-2 py-1 text-sm border rounded cursor-pointer ${
                          selected.includes(opt.ref)
                            ? 'border-brand-400 bg-brand-50 text-brand-800'
                            : 'border-gray-200 text-gray-700 hover:bg-gray-50'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selected.includes(opt.ref)}
                          onChange={() => toggle(opt.ref)}
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {/*
              후보에 없는 참조 — 조직 커스텀 역할(`co-<cuid>`)·보관된 역할·다른 환경에서 들어온 값이
              그 모양으로 온다. **가리지 않는다**(숨기면 운영자가 「대상이 비어 있다」고 믿는데 서버는
              그 제약으로 거른다). 끄는 것만 할 수 있게 둔다 — 그 값을 다시 고를 후보가 없으므로.
            */}
            {unlisted.missing.length > 0 && (
              <div>
                <div className="font-medium text-sm text-gray-700 mb-2">
                  후보에 없는 대상(보관·조직 전용 역할)
                </div>
                <div className="flex flex-wrap gap-2">
                  {unlisted.missing.map((ref) => (
                    <button
                      key={ref}
                      type="button"
                      onClick={() => toggle(ref)}
                      className="px-2 py-1 text-sm border border-gray-300 bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
                      title="클릭하면 제거합니다"
                    >
                      {audienceLabel(ref, groups)} ✕
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/*
              ⚠️ **다른 통이다** — 그 체계의 목록을 못 받아(403) 후보에 없어 보이는 것이고, 값 자체는
              정상 제약일 수 있다. 위 통과 섞으면 운영자가 「없어진 역할」로 읽고 지운다 ⇒ 노출이
              넓어진다(사유는 `splitUnlistedAudiences` JSDoc). 그래서 **제거를 막는다**: ✕를 떼고
              버튼을 `disabled`로 둔다. 숨기지도 않는다 — 무엇이 걸려 있는지는 보여야 한다.
            */}
            {unlisted.unloadable.length > 0 && (
              <div>
                <div className="font-medium text-sm text-gray-700 mb-2">
                  불러오지 못한 체계의 대상(지우지 마세요)
                </div>
                <div className="flex flex-wrap gap-2">
                  {unlisted.unloadable.map((ref) => (
                    <button
                      key={ref}
                      type="button"
                      disabled
                      className="px-2 py-1 text-sm border border-gray-300 bg-gray-50 text-gray-400 rounded cursor-not-allowed"
                      title="역할 목록을 불러오지 못해 이 대상이 유효한지 확인할 수 없습니다 — 제거할 수 없습니다."
                    >
                      {audienceLabel(ref, groups)}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/*
          지금 선택이 무엇을 뜻하는지 — 빈 선택과 「아무에게도」를 문구로 가른다.

          ## 🔴 2026-10-21 — 「역할에게만 보입니다」는 **거짓이 됐다**(결정 채택 2)
          공개 업데이트 내역 페이지가 `audiences` 판정을 **적용하지 않게 됐다**(「페이지는 전부 ·
          푸시는 타겟팅」 — `release-notes.service.ts`의 `getPublicReleaseNotes` 🔴 절) ⇒ 역할을
          골라도 **노출이 좁혀지지 않는다.** 종전 문구 둘을 그대로 두면 운영자가 「운영팀만 볼
          안내」를 그 설정으로 저장하고 **숨겨졌다고 믿는데 공개 변경 이력에 그대로 뜬다** —
          **거짓 UI 보증이 노출을 만든다.** 그래서 ① 「특정 역할에게만 보이려면 위에서 고르세요」를
          **지웠고** ② 「N개 역할에게만 보입니다」를 **「저장됩니다 + 페이지는 좁혀지지 않습니다」**로
          바꿨다. ⚠️ tsc·spec이 이 축을 **원리적으로 못 잡는다**(숫자는 맞고 라벨만 거짓이다).

          ## ⚠️ 공지 쪽 같은 문구를 **함께 고치지 말 것** — 그쪽은 **참이다**
          `pages/Announcements.tsx`의 쌍둥이 문구(「N개 역할에게만 보입니다」는 **글자까지 같다**)는
          그대로 둔다: 공지는 `announcements.service.ts`가 `matchesAudiences`로 **실제로 거른다**
          (결정의 데이터별 표 — 공지는 역할로 필터링한다). 두 surface가 **의도적으로 갈린** 자리다.
          ⚠️ 앞부분은 원래도 달랐다 — 공지는 「**고른 화면의** 모두에게」로 **플랫폼 축이 하나 더**
          있다. 즉 **공지는 플랫폼 ∧ 역할 둘로 좁히고 노트는 플랫폼만으로 좁힌다** — 그 차이가
          「왜 갈렸나」의 단서다.

          ## ⚠️ 화면 문구에 **용도(푸시·배지)를 적지 않았다**
          채택 3(배지)·5(팝업)는 **미구현**이므로 「앞으로 푸시·배지에 쓰입니다」는 **지금 거짓**이고
          운영자는 그것을 「곧 좁혀진다」로 읽는다 ⇒ **거짓 보증이 모양만 바꿔 남는다.** 용도는
          이 주석에만 적는다(주석은 개발자가 읽고 화면은 운영자가 **지금의 사실로** 읽는다).

          ## 이 설정의 입구를 잠글지 — **이 파일 머리 docblock이 정본이다**
          ⚠️ 여기 그 논거를 **다시 적지 말 것.** 1차 작성에서 같은 서술이 **두 자리**(그 docblock +
          여기)에 있었고, 그것은 「수를 적은 자리가 둘」과 **같은 모양**이다 — 사용자가 그 정책을
          확정하는 날 한쪽만 고치면 **다른 쪽이 조용히 낡는다.** 그 판단은 **열려 있고**(효과 없는
          설정을 남길지) 근거·되짚을 시점은 그 docblock 한 벌이 소유한다.
        */}
        <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700">
          {selected.length === 0 ? (
            <>
              <strong>모두에게 보입니다.</strong> (아무에게도 보이지 않게 하려면 노출 대상이 아니라
              노트 파일의 surface를 <code>INTERNAL</code>로 적습니다 — 이 화면에서는 바꿀 수 없습니다.)
            </>
          ) : (
            <>
              <strong>{selected.length}개 역할</strong>이 노출 대상으로 <strong>저장</strong>됩니다.
              <br />
              ⚠️ <strong>업데이트 내역 페이지는 이 설정으로 좁혀지지 않습니다</strong> — 역할과
              무관하게 전원에게 보입니다.
            </>
          )}
        </div>

        {needsAcknowledgement && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded text-sm text-amber-800">
            {/*
              🔴 2026-10-21 — 이 경고의 **전제가 반쪽이 됐다**(결정 채택 2). 「좁히지 않으면 전원이
              본다」는 **좁혀도 전원이 보는** 지금 상태에서 「좁히면 달라진다」로 읽힌다 ⇒ 그 함축이
              거짓이라 **한 줄을 더 적는다.** 문구를 지우지 않은 이유: 서버가 같은 상태를 400으로
              거부하므로(`AUDIENCE_NAMESPACE_UNCONSTRAINED`) 이 체크박스가 **그 거부를 받아 주는
              유일한 자리**다(위 docblock의 「막지 않는다로 되돌리지 말 것」).
              ⚠️ 그 400의 **오늘의 소유자는 없다** — 그것이 겨누던 축의 주인은 채택 3(배지)·5(팝업)로
              가는데 **둘 다 미구현**이다(서버 쪽 전문: `release-notes.service.ts`의
              `unconstrainedReachSentence` JSDoc). 「푸시·배지가 소유한다」로 적지 말 것.
            */}
            <p>
              {withObjectParticle(unconstrained.map((g) => g.label).join(' · '))} 좁히지 않았으므로 <strong>그 체계의
              전원</strong>이 여전히 대상입니다. 의도한 것이 아니면 그 체계에서도 역할을 고르세요.
            </p>
            <p className="mt-1">
              ⚠️ 이 승인은 <strong>페이지 노출을 바꾸지 않습니다</strong> — 좁히든 좁히지 않든
              업데이트 내역 페이지는 전원에게 보입니다.
            </p>
            {/*
              ⚠️ 이 체크박스가 **저장을 막는다** — 문구만 두면 서버가 400으로 거부해 운영자가
              막다른 길에 선다(위 `needsAcknowledgement` JSDoc). 라벨을 「확인했습니다」로만 두지
              않고 **무엇을 승인하는지**를 적는다 — 「확인」만 있으면 고무도장이 된다.
            */}
            <label className="mt-2 flex items-start gap-2 font-medium">
              <input
                type="checkbox"
                className="mt-0.5"
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={saving}
              />
              <span>
                {unconstrained.map((g) => g.label).join(' · ')} 전원에게 보여도 됩니다 — 알고 저장합니다.
              </span>
            </label>
          </div>
        )}

      </>
    </Modal>
  );
}

/**
 * 푸터는 **별도 컴포넌트여야 한다** — `useModalRequestClose`는 `Modal` children **하위**에서만 값을
 * 잡는다(부모 본문에서 부르면 `null`이라 dirty 확인이 우회된다). 선례의 그 JSDoc과 같은 이유다
 * (`company-roles/CompanyRoleFormModal.tsx`의 `FormFooter`).
 *
 * ⚠️ 취소가 `onClose`를 **직접 부르지 않는다** — `requestClose`를 거쳐야 Esc·오버레이·✕와 **같은
 * 경로**로 수렴해 dirty 확인을 받는다. `?? onClose` 폴백은 훅이 컨텍스트 밖일 때의 안전망이고
 * (그 상태면 dirty 확인이 없는 것이 맞다) 선례와 같은 형태다.
 */
function AudienceModalFooter({
  saving,
  onClose,
  onSave,
  blocked,
}: {
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
  /** 승인 체크 전이다 — 서버가 400을 낼 상태이므로 **여기서 막는다**(막다른 길 방지) */
  blocked: boolean;
}) {
  const requestClose = useModalRequestClose();
  return (
    <>
      <Button variant="secondary" size="sm" onClick={() => (requestClose ?? onClose)()} disabled={saving}>
        취소
      </Button>
      {/*
        ⚠️ 빈 선택에서도 저장이 열려 있다 — 그것이 설정을 해제하는 유일한 길이다(`blocked`는
        빈 선택에서 **거짓**이다: 아무 체계도 좁히지 않았으면 승인할 것이 없다).
        ⚠️ 그런데 「한쪽 체계만」 좁힌 상태는 **막는다** — 서버가 그것을 거부하므로 열어 두면
        운영자가 저장을 누르고 400만 본다. `title`을 함께 주는 이유는 **왜 비활성인지**가
        버튼만 보면 안 보이기 때문이다(경고 블록이 스크롤 밖일 수 있다).
      */}
      <Button
        variant="primary"
        size="sm"
        onClick={onSave}
        disabled={saving || blocked}
        title={blocked ? '좁히지 않은 체계가 있습니다 — 위 확인란을 체크하거나 그 체계의 역할을 고르세요.' : undefined}
      >
        {saving ? '저장 중...' : '저장'}
      </Button>
    </>
  );
}
